/**
 * The ops app's own frame: a slim bar (back to the app's home, the screen's name, who is signed in, sign out) and
 * the screen under it — no sidebar, no tabs, no main menu. Signing out goes back to the app's own sign-in.
 */
import { Suspense } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowRight, SignOut } from '@phosphor-icons/react'
import { AliensMark, Button, PageSkeleton } from '@/design-system'
import { NetworkBanner, UpdatePrompt } from '@/layout/SystemBanners'
import { useAuth } from '@/stores/auth'
import { OPS_HOME, OPS_LOGIN, opsTitle } from './opsApp'

export function RequireOpsAuth() {
  const status = useAuth((s) => s.status)
  const location = useLocation()
  if (status === 'unknown') return <PageSkeleton />
  if (status === 'anonymous') return <Navigate to={OPS_LOGIN} replace state={{ from: location.pathname + location.search }} />
  return <Outlet />
}

export function OpsShell() {
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const qc = useQueryClient()
  const go = useNavigate()
  const { pathname } = useLocation()
  const home = pathname === OPS_HOME || pathname === `${OPS_HOME}/`
  const signOut = async () => {
    await logout()
    qc.clear()
    go(OPS_LOGIN, { replace: true })
  }
  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-border bg-surface px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
        {home ? <AliensMark className="h-7 w-9" /> : <Button variant="ghost" icon={ArrowRight} onClick={() => go(OPS_HOME)} aria-label="رجوع" />}
        <b className="min-w-0 flex-1 truncate text-base">{home ? 'التشغيل' : opsTitle(pathname)}</b>
        <span className="max-w-[40%] truncate text-xs text-fg-muted">{user?.displayName ?? user?.username}</span>
        <Button variant="ghost" icon={SignOut} onClick={() => void signOut()} aria-label="تسجيل الخروج" />
      </header>
      <NetworkBanner />
      <main className="min-w-0 flex-1">
        <Suspense fallback={<PageSkeleton />}><Outlet /></Suspense>
      </main>
      <UpdatePrompt />
    </div>
  )
}
